import { ImpersonateOnMobileDialog } from '@/components/impersonation/impersonate-on-mobile-dialog';
import { PageContent, PageHeader, PageLayout } from '@/components/ui/design-system';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  clearImpersonationState,
  setImpersonationToken,
  storeAdminSession,
} from '@/features/impersonation-session';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { Route as AppRoute } from '@/routes/_app/route';
import { getInternalUsers, impersonateUser } from '@/services/api-allo/internal';
import { supabase } from '@/services/supabase';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

const internalUsersSearchSchema = z.object({
  page: fallback(z.coerce.number().catch(0), 0),
  search: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute('/_app/internal/users')({
  validateSearch: zodValidator(internalUsersSearchSchema),
  beforeLoad: async () => {
    try {
      await getInternalUsers({ page: 0, size: 1 });
    } catch (error) {
      if (error instanceof Error && error.message === 'FORBIDDEN') {
        throw redirect({ to: '/', replace: true });
      }
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const { user } = AppRoute.useLoaderData();
  const { page, search } = Route.useSearch();
  const [searchInput, setSearchInput] = useState(search ?? '');
  const searchRef = useRef(search);
  const [mobileDialogUser, setMobileDialogUser] = useState<{ id: string; email: string } | null>(
    null,
  );

  useEffect(() => {
    searchRef.current = search;
    setSearchInput(search ?? '');
  }, [search]);

  const navigateDebounced = useDebouncedCallback(
    useCallback(
      (value: string) => {
        if ((searchRef.current ?? '') === value) {
          return;
        }
        router.navigate({
          to: '/internal/users',
          search: { page: 0, search: value || undefined },
          replace: true,
        });
      },
      [router],
    ),
    350,
  );

  const usersQuery = useQuery({
    queryKey: ['internal-users', page, search ?? ''],
    queryFn: () =>
      getInternalUsers({
        page,
        search,
      }),
    retry: false,
  });

  useEffect(() => {
    if (usersQuery.error instanceof Error && usersQuery.error.message === 'FORBIDDEN') {
      router.navigate({ to: '/', replace: true });
    }
  }, [router, usersQuery.error]);

  const impersonationMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const session = await supabase.auth.getSession();
      const currentSession = session.data.session;

      if (!currentSession?.access_token) {
        throw new Error('UNAUTHORIZED');
      }

      storeAdminSession({
        accessToken: currentSession.access_token,
        refreshToken: currentSession.refresh_token,
        adminName: user.email,
        adminEmail: user.email,
        adminPid: user.id,
      });

      const accessToken = await impersonateUser(targetUserId);
      setImpersonationToken(accessToken);
    },
    onSuccess: () => {
      window.location.href = '/';
    },
    onError: (error) => {
      clearImpersonationState();
      toast.error(error instanceof Error ? error.message : 'Could not impersonate user');
    },
  });

  const rows = usersQuery.data?.data ?? [];
  const pagination = usersQuery.data?.metadata?.pagination;
  const totalPages = pagination?.total_pages ?? 0;
  const currentPage = pagination?.current_page ?? page;

  return (
    <PageLayout>
      <PageHeader title="Supervision" />
      <PageContent>
        <div className="flex h-full flex-col gap-4 p-4">
          <div className="flex flex-col gap-4 h-full">
            <label className="group flex h-11 w-full max-w-md items-center gap-3 rounded-2xl border border-primary/10 bg-white px-3.5 transition-colors focus-within:border-primary/30">
              <Search className="size-4 text-primary/35 transition-colors group-focus-within:text-primary/60" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  navigateDebounced(event.target.value);
                }}
                placeholder="Search by name, email, or user ID"
                className="h-full w-full bg-transparent text-body-14 text-primary outline-none placeholder:text-primary/35"
              />
            </label>

            <div className="min-h-0 flex-1 overflow-auto rounded-2xl border border-primary/10 bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="border-primary/10">
                    <TableHead className="h-12 px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Name
                    </TableHead>
                    <TableHead className="px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Email
                    </TableHead>
                    <TableHead className="px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Phone
                    </TableHead>
                    <TableHead className="px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Account Type
                    </TableHead>
                    <TableHead className="px-4 text-right text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersQuery.isLoading ? (
                    <TableRow className="border-primary/10">
                      <TableCell colSpan={5} className="px-4 py-12 text-center">
                        <div className="space-y-1">
                          <div className="text-body-14 text-primary">Loading users</div>
                          <div className="text-body-14 text-primary/50">
                            Pulling the latest internal directory entries.
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : usersQuery.error ? (
                    <TableRow className="border-primary/10">
                      <TableCell colSpan={5} className="px-4 py-12 text-center">
                        <div className="space-y-1">
                          <div className="text-body-14 text-primary">Could not load users</div>
                          <div className="text-body-14 text-primary/50">
                            Refresh the page or adjust the search query and try again.
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow className="border-primary/10">
                      <TableCell colSpan={5} className="px-4 py-12 text-center">
                        <div className="space-y-1">
                          <div className="text-body-14 text-primary">No users found</div>
                          <div className="text-body-14 text-primary/50">
                            Try a broader name or email search.
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className="border-primary/10 bg-white transition-colors hover:bg-primary/[0.02]"
                      >
                        <TableCell className="px-4 py-3.5">
                          <div className="font-medium text-primary">{row.name ?? 'Unknown'}</div>
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-primary/60">
                          {row.email ?? 'No email'}
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-primary/60">
                          {row.phone ?? 'No phone'}
                        </TableCell>
                        <TableCell className="px-4 py-3.5">
                          <span className="inline-flex rounded-full border border-primary/10 bg-primary/[0.04] px-2.5 py-1 text-body-12 text-primary/70">
                            {row.account_type ?? 'No role'}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-right">
                          <div className="inline-flex gap-2">
                            <button
                              type="button"
                              className="inline-flex h-9 items-center justify-center rounded-md border border-primary/15 bg-white px-3.5 text-body-14 text-primary transition-colors hover:bg-primary/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={impersonationMutation.isPending}
                              onClick={() => impersonationMutation.mutate(row.id)}
                            >
                              {impersonationMutation.isPending ? 'Starting...' : 'Impersonate'}
                            </button>
                            <button
                              type="button"
                              className="inline-flex h-9 items-center justify-center rounded-md border border-primary/15 bg-white px-3.5 text-body-14 text-primary transition-colors hover:bg-primary/[0.04]"
                              onClick={() =>
                                setMobileDialogUser({ id: row.id, email: row.email ?? '' })
                              }
                            >
                              Mobile
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-body-14 text-primary/50">
                Page <span className="font-semibold text-primary">{currentPage + 1}</span>
                {totalPages > 0 ? (
                  <>
                    {' '}
                    of <span className="font-semibold text-primary">{totalPages}</span>
                  </>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-primary/10 bg-white px-3 text-body-14 text-primary transition-colors hover:bg-primary/[0.04] disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={currentPage <= 0}
                  onClick={() =>
                    router.navigate({
                      to: '/internal/users',
                      search: {
                        page: Math.max(0, currentPage - 1),
                        search: search || undefined,
                      },
                    })
                  }
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </button>

                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-primary/10 bg-white px-3 text-body-14 text-primary transition-colors hover:bg-primary/[0.04] disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={totalPages > 0 ? currentPage + 1 >= totalPages : rows.length === 0}
                  onClick={() =>
                    router.navigate({
                      to: '/internal/users',
                      search: {
                        page: currentPage + 1,
                        search: search || undefined,
                      },
                    })
                  }
                >
                  Next
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {mobileDialogUser && (
          <ImpersonateOnMobileDialog
            targetUserId={mobileDialogUser.id}
            targetEmail={mobileDialogUser.email}
            open
            onOpenChange={(open) => {
              if (!open) {
                setMobileDialogUser(null);
              }
            }}
          />
        )}
      </PageContent>
    </PageLayout>
  );
}
